import React, { ReactNode } from "react";
import { ReportDateRangePicker } from "./ReportDateRangePicker";
import type { ReportDatePreset } from "../utils/reportDateRange";

interface ReportPageLayoutProps {
  /** When omitted or empty, the title heading is not rendered */
  title?: string;
  /** Optional inline content after the title (e.g. " · Balance: $117.03") */
  titleExtra?: ReactNode;
  datePreset?: ReportDatePreset;
  onDatePresetChange?: (preset: ReportDatePreset, from: string, to: string) => void;
  children: ReactNode;
  /** Optional breadcrumb above title (e.g. "Finance / Cash Drawer") */
  breadcrumb?: ReactNode;
  /** Optional KPI row above content */
  kpiRow?: ReactNode;
  /** Optional export button (e.g. CSV) */
  exportButton?: ReactNode;
  /** Optional full second row (e.g. tabs + date + export). When set, datePreset/onDatePresetChange/exportButton are not rendered in the layout. */
  toolbar?: ReactNode;
  /** When true, use 8px vertical spacing around toolbar instead of 12px/16px (for denser operational pages) */
  compactToolbar?: boolean;
  /** Custom margin (px) above the toolbar row. When set, overrides compactToolbar spacing for top. */
  toolbarMarginTop?: number;
  /** Custom margin (px) below the toolbar row. When set, overrides compactToolbar spacing for bottom. */
  toolbarMarginBottom?: number;
  /** Error message to show (e.g. "This store does not have access to reports") */
  error?: string | null;
  /** When true, layout fills viewport height and children use flex-1 min-h-0 (no page scroll; list pages like Cash Drawer) */
  fillHeight?: boolean;
  /** When true, do not render the page header (title/breadcrumb/toolbar row); use when app header already shows the title */
  hideTitle?: boolean;
  /** When true, apply negative top margin to cancel DefaultLayout's pt-3 so tabs sit flush under the app header */
  compactTop?: boolean;
}

export function ReportPageLayout({
  title,
  titleExtra,
  datePreset,
  onDatePresetChange,
  children,
  breadcrumb,
  kpiRow,
  exportButton,
  toolbar,
  compactToolbar = false,
  toolbarMarginTop,
  toolbarMarginBottom,
  error,
  fillHeight = false,
  hideTitle = false,
  compactTop = false,
}: ReportPageLayoutProps) {
  const showDefaultHeaderRow = toolbar == null && datePreset != null && onDatePresetChange != null;
  const useCustomToolbarMargin = toolbarMarginTop != null || toolbarMarginBottom != null;
  const defaultTop = compactToolbar ? 8 : 12;
  const defaultBottom = compactToolbar ? 8 : 16;
  const toolbarWrapClass = useCustomToolbarMargin
    ? "w-full min-w-0 border-b border-[#e5e7eb] pb-0"
    : compactToolbar
      ? "mt-2 w-full min-w-0 border-b border-[#e5e7eb] pb-2 mb-2"
      : "mt-3 w-full min-w-0 border-b border-[#e5e7eb] pb-0 mb-4";
  const toolbarWrapStyle = useCustomToolbarMargin
    ? { marginTop: toolbarMarginTop ?? defaultTop, marginBottom: toolbarMarginBottom ?? defaultBottom }
    : undefined;
  const rootClass = [
    fillHeight
      ? "w-full max-w-full max-md:px-0 md:px-3 pt-0 pb-0 flex flex-col flex-1 min-h-0 -mb-[10px]"
      : "w-full max-w-full max-md:px-0 md:px-3 pt-0 pb-0 flex flex-col flex-1 min-h-[min-content]",
    compactTop && "-mt-3",
  ]
    .filter(Boolean)
    .join(" ");
  /* fillHeight pages fill the viewport — skip extra pb. Scrolling report pages need more past-last-card clearance. */
  const contentClass = fillHeight
    ? "flex flex-col min-w-0 flex-1 min-h-0"
    : "flex flex-col min-w-0 flex-1 min-h-[min-content] max-md:pb-20";

  const hasToolbarOrDateRow = toolbar != null || showDefaultHeaderRow;
  const showFullHeader = !hideTitle;
  const showCompactHeader = hideTitle && hasToolbarOrDateRow;

  return (
    <div className={rootClass}>
      {showFullHeader && (
        <div className={`page-header ${useCustomToolbarMargin ? "mb-0" : compactToolbar ? "mb-2" : "mb-3"} ${fillHeight ? "shrink-0" : ""}`}>
          {breadcrumb != null && (
            <p className="text-sm text-[#6b7280] mb-1" aria-hidden="true">
              {breadcrumb}
            </p>
          )}
          {(title != null && title !== "") && (
            <h1 className="text-xl font-semibold text-[#111827]">
              {title}
              {titleExtra != null && <span className="font-normal text-[#6b7280]">{titleExtra}</span>}
            </h1>
          )}
          {toolbar != null && (
            <div className={`flex items-center justify-between gap-4 ${toolbarWrapClass}`} style={toolbarWrapStyle}>
              {toolbar}
            </div>
          )}
          {showDefaultHeaderRow && (
            <div className={`flex flex-wrap items-center justify-between gap-4 ${(title != null && title !== "") || breadcrumb != null ? "mt-3" : ""}`}>
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                <span className="inline-block min-w-[150px]">
                  <ReportDateRangePicker value={datePreset!} onChange={onDatePresetChange!} />
                </span>
                {exportButton}
              </div>
            </div>
          )}
        </div>
      )}
      {showCompactHeader && (
        <div className="w-full min-w-0 border-b border-[#e5e7eb] pb-2 mb-2 shrink-0">
          {toolbar != null && (
            <div className="flex min-w-0 w-full items-center justify-between gap-4" style={toolbarWrapStyle}>
              {toolbar}
            </div>
          )}
          {showDefaultHeaderRow && (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                <span className="inline-block min-w-[150px]">
                  <ReportDateRangePicker value={datePreset!} onChange={onDatePresetChange!} />
                </span>
                {exportButton}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {kpiRow && <div className="mb-4">{kpiRow}</div>}

      <div className={contentClass}>{children}</div>
    </div>
  );
}
